/**
 * Local session continuity for Printing Toolkit.
 * Survives refresh / mode switches without clearing order work.
 */

export const PRINTING_TOOLKIT_SESSION_KEY = 'lsc.printingToolkit.session.v1';

export const PRINTING_TOOLKIT_MODES = Object.freeze({
  PACK: 'pack',
  OTHER: 'other',
  QUEUE: 'queue',
});

export const PRINTING_TOOLKIT_OTHER_TABS = Object.freeze([
  'labels',
  'inscriptions',
  'general',
]);

const OTHER_TAB_SET = new Set(PRINTING_TOOLKIT_OTHER_TABS);

export const DEFAULT_PRINTING_TOOLKIT_SESSION = Object.freeze({
  version: 1,
  mode: PRINTING_TOOLKIT_MODES.PACK,
  otherTab: 'labels',
  /** @deprecated folded into Pack; kept so old sessions still parse */
  ordersTab: 'orders',
  /** @deprecated list is driven by selectedFolderId; kept for print-count day fallback */
  filterDate: '',
  selectedFolderId: '',
  /** all = OrderDesk folder membership; delivery/dispatch = narrow to folder date */
  dateFilterField: 'all',
  filterShipping: '',
  filterCompany: '',
  searchId: '',
  selectedIds: {},
  orderCopies: {},
  generalSizeKey: 'A4',
  generalCopies: 1,
  generalPageRanges: '',
  generalNotes: '',
  updatedAt: null,
});

export function createDefaultPrintingToolkitSession() {
  return {
    ...DEFAULT_PRINTING_TOOLKIT_SESSION,
    selectedIds: {},
    orderCopies: {},
    updatedAt: new Date().toISOString(),
  };
}

export function migratePrintingToolkitMode(value) {
  const rawMode = value?.mode;
  const ordersTab = value?.ordersTab;
  const savedOther = OTHER_TAB_SET.has(value?.otherTab) ? value.otherTab : null;

  if (rawMode === PRINTING_TOOLKIT_MODES.QUEUE || rawMode === 'queue') {
    return {
      mode: PRINTING_TOOLKIT_MODES.QUEUE,
      otherTab: savedOther || 'labels',
    };
  }

  if (rawMode === 'general') {
    return { mode: PRINTING_TOOLKIT_MODES.OTHER, otherTab: 'general' };
  }

  if (rawMode === PRINTING_TOOLKIT_MODES.OTHER || rawMode === 'other') {
    const fromOrders =
      ordersTab === 'labels' || ordersTab === 'inscriptions' || ordersTab === 'general'
        ? ordersTab
        : 'labels';
    return {
      mode: PRINTING_TOOLKIT_MODES.OTHER,
      otherTab: savedOther || fromOrders,
    };
  }

  if (ordersTab === 'labels' || ordersTab === 'inscriptions' || ordersTab === 'general') {
    return { mode: PRINTING_TOOLKIT_MODES.OTHER, otherTab: ordersTab };
  }

  return {
    mode: PRINTING_TOOLKIT_MODES.PACK,
    otherTab: savedOther || 'labels',
  };
}

export function sanitizePrintingToolkitSession(value) {
  const base = createDefaultPrintingToolkitSession();
  if (!value || typeof value !== 'object') return base;
  const migrated = migratePrintingToolkitMode(value);
  // Historic `delivery` meant Packers dateField API load (hid week-folder members
  // with a different Delivery day). Printing now loads membership; default narrow is all.
  const dateFilterField =
    value.dateFilterField === 'dispatch'
      ? 'dispatch'
      : value.dateFilterField === 'delivery' && value.folderDateNarrow === true
        ? 'delivery'
        : 'all';
  return {
    ...base,
    ...value,
    version: 1,
    mode: migrated.mode,
    otherTab: migrated.otherTab,
    ordersTab: 'orders',
    filterDate: String(value.filterDate || ''),
    selectedFolderId: String(value.selectedFolderId || ''),
    dateFilterField,
    folderDateNarrow:
      dateFilterField === 'delivery' || dateFilterField === 'dispatch',
    filterShipping: String(value.filterShipping || ''),
    filterCompany: String(value.filterCompany || ''),
    searchId: String(value.searchId || ''),
    selectedIds:
      value.selectedIds && typeof value.selectedIds === 'object' && !Array.isArray(value.selectedIds)
        ? { ...value.selectedIds }
        : {},
    orderCopies:
      value.orderCopies && typeof value.orderCopies === 'object' && !Array.isArray(value.orderCopies)
        ? { ...value.orderCopies }
        : {},
    generalSizeKey: String(value.generalSizeKey || 'A4'),
    generalCopies: Math.max(1, Math.min(99, Number(value.generalCopies) || 1)),
    generalPageRanges: String(value.generalPageRanges || ''),
    generalNotes: String(value.generalNotes || ''),
    updatedAt: value.updatedAt || new Date().toISOString(),
  };
}

export function loadPrintingToolkitSession() {
  try {
    if (typeof localStorage === 'undefined') return createDefaultPrintingToolkitSession();
    const raw = localStorage.getItem(PRINTING_TOOLKIT_SESSION_KEY);
    if (!raw) return createDefaultPrintingToolkitSession();
    return sanitizePrintingToolkitSession(JSON.parse(raw));
  } catch {
    return createDefaultPrintingToolkitSession();
  }
}

export function savePrintingToolkitSession(next) {
  const sanitized = sanitizePrintingToolkitSession({
    ...next,
    folderDateNarrow:
      next?.dateFilterField === 'delivery' || next?.dateFilterField === 'dispatch',
    updatedAt: new Date().toISOString(),
  });
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PRINTING_TOOLKIT_SESSION_KEY, JSON.stringify(sanitized));
    }
  } catch {
    // Quota / private mode — keep in-memory only.
  }
  return sanitized;
}
